import { FC, useCallback, useState, FocusEvent, ChangeEvent } from 'react';
import { Environment } from 'react-relay/legacy';
import { defineMessages, useIntl } from 'dibs-react-intl';
import { AddressAutocomplete } from 'dibs-elements/exports/AddressAutocomplete';

import useAddressAutocomplete, { Suggestion } from '../../hooks/useAddressAutocomplete';

import styles from '../../styles/main.scss';

type Props = {
    value: string;
    error: string;
    autoFocus: boolean;
    fieldSize: 'small' | 'medium' | 'large';
    isBlurred: boolean;
    disabled: boolean;
    onChange: (arg: { name?: string; value?: string }) => void;
    onBlur: (arg: { name?: string; value?: string }) => void;
    onFocus: (arg: { name?: string; value?: string }) => void;
    onDetailsReceived: (details: {
        country?: string | null;
        city?: string | null;
        state?: string | null;
        zipCode?: string | null;
    }) => void;
    environment: Environment;
};

const messages = defineMessages({
    placeholder: {
        id: 'dc.AddressForm.Line1Autocomplete.placeholder',
        defaultMessage: 'Address*',
    },
});

const fieldName = 'line1';

const AddressLineAutocomplete: FC<Props> = ({
    value,
    error,
    autoFocus,
    fieldSize,
    isBlurred,
    disabled,
    onChange,
    onBlur,
    onFocus,
    onDetailsReceived,
    environment,
}) => {
    const intl = useIntl();

    const { suggestions, getAddressDetails } = useAddressAutocomplete({
        input: value,
        environment,
        placeType: 'address',
    });
    const [isFocused, setIsFocused] = useState(false);

    const handleSelect = useCallback(
        async (selected?: Suggestion | null): Promise<void> => {
            const { addressLine } = selected || {};
            onChange({ name: fieldName, value: addressLine || '' });

            const addressDetails = await getAddressDetails(selected ?? null);
            const { country, city, stateOrRegion, zipCode } = addressDetails || {};

            onDetailsReceived({
                country: country || '',
                city: city || '',
                state: stateOrRegion || '',
                zipCode: zipCode || '',
            });
        },
        [onChange, getAddressDetails, onDetailsReceived]
    );

    const handleBlur = useCallback(
        (event: FocusEvent<HTMLInputElement>): void => {
            setIsFocused(false);
            onBlur({ name: fieldName, value: event.target.value });
        },
        [onBlur]
    );

    const handleChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            onChange({ name: fieldName, value: event.target.value });
        },
        [onChange]
    );

    const handleFocus = useCallback(
        (event: FocusEvent<HTMLInputElement>): void => {
            setIsFocused(true);
            onFocus({ name: fieldName, value: event.target.value });
        },
        [onFocus]
    );

    const showError = !disabled && isBlurred && !!error && !isFocused;
    const hasValidText = isBlurred && !!value && !showError;

    return (
        <AddressAutocomplete<Suggestion>
            value={value}
            options={suggestions || []}
            dataTn="line-1-autocomplete"
            name={fieldName}
            errorMessage={showError ? error : undefined}
            hasValidText={hasValidText}
            autoComplete="address-line1"
            autoFocus={autoFocus}
            size={fieldSize}
            disabled={disabled}
            className={styles.input}
            placeholder={intl.formatMessage(messages.placeholder)}
            onSelect={handleSelect}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            hasAnimatedPlaceholder
        />
    );
};

export default AddressLineAutocomplete;
